import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Sparkles, Search, RefreshCw, Calendar, Play, Trash2, AlertCircle, CheckCircle2, Clock, X,
  Briefcase, TrendingUp, DollarSign, Zap, Tag, ChevronRight, ExternalLink, Building2, MapPin, Users
} from 'lucide-react';
import { leadGenAPI, dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Lead Gen Agent — three sub-tabs:
 *   1. Find New      — Apollo people-search → preview candidates → import as leads.
 *   2. Enrich        — Pick leads by date/year/stage/BDA → dry-run preview → apply.
 *   3. Schedules     — Cron-driven runs (search or enrich), with run history.
 *
 * Everything is backed by /api/lead-gen/* on the backend, which in turn
 * uses APOLLO_API_KEY in the .env to call Apollo's REST endpoints.
 */

const TABS = [
  { key: 'find', label: 'Find New Leads', icon: Search, blurb: 'Find solar companies hiring for control-room / ROCC roles via Gemini + Google Search. Free, real data, real job postings.' },
  { key: 'enrich', label: 'Enrich Existing', icon: RefreshCw, blurb: 'Fill missing fields on existing leads using Apollo (filtered by date / year / stage / BDA). Works on the Apollo free tier.' },
  { key: 'schedules', label: 'Schedules & Runs', icon: Calendar, blurb: 'Cron-triggered automation + full run history for every search, research and enrich invocation.' }
];

// AMG's ICP role titles — companies actively hiring for any of these are
// solar IPPs/operators with a remote operations center.
const DEFAULT_SOLAR_ROLE_TITLES = [
  'Head of Asset Management',
  'Asset Manager',
  'Technical Asset Manager',
  'Portfolio Manager',
  'VP Operations',
  'Director of Operations',
  'O&M Manager',
  'Performance Manager',
  'Head of Energy Storage',
  'BESS Operations Manager',
  'Renewable Operations Director'
];

const INPUT_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent';
const LABEL_CLS = 'block text-xs font-medium text-gray-700 mb-1';
const BTN_PRIMARY = 'px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium';
const BTN_SECONDARY = 'px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm';

// ─── Reusable: comma-separated chip input ──────────────────────────────────
function ChipInput({ label, value, onChange, placeholder, hint }) {
  // `value` is an array; we render a comma-joined input for simplicity.
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <input
        type="text"
        value={(value || []).join(', ')}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
        className={INPUT_CLS}
      />
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

// ─── Apollo health pill (top-right) ─────────────────────────────────────────
function ApolloHealthPill() {
  const [state, setState] = useState({ loading: true, healthy: false });
  useEffect(() => {
    leadGenAPI.health()
      .then((r) => setState({ loading: false, healthy: !!(r?.apollo?.healthy && r?.apollo?.is_logged_in), raw: r }))
      .catch(() => setState({ loading: false, healthy: false }));
  }, []);
  if (state.loading) return <span className="text-xs text-gray-400">Checking Apollo…</span>;
  return state.healthy ? (
    <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
      <CheckCircle2 size={12} /> Apollo connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
      <AlertCircle size={12} /> Apollo unreachable
    </span>
  );
}

// Lead-discovery signal modes — each one tells Gemini what kind of solar
// company to surface. Maps to the same signal keys in geminiLeadResearch.js.
// Each carries an icon + accent colour so the picker renders as a card grid.
const SIGNAL_OPTIONS = [
  {
    value: 'hiring', label: 'Hiring',
    title: 'Hiring for ROCC roles',
    hint: 'Companies posting Solar Performance / Control-Room jobs — they operate plants.',
    icon: Briefcase,
    accent: 'from-emerald-500 to-emerald-600',
    accentText: 'text-emerald-700',
    accentBg: 'bg-emerald-50',
    accentBorder: 'border-emerald-200'
  },
  {
    value: 'ma', label: 'M&A',
    title: 'Mergers & acquisitions',
    hint: 'Solar IPPs/EPCs that recently bought, sold, or got acquired (last 12mo).',
    icon: TrendingUp,
    accent: 'from-purple-500 to-purple-600',
    accentText: 'text-purple-700',
    accentBg: 'bg-purple-50',
    accentBorder: 'border-purple-200'
  },
  {
    value: 'funding', label: 'Funding',
    title: 'Recently raised funding',
    hint: 'Solar companies that closed Series A-E, debt facilities, or project finance.',
    icon: DollarSign,
    accent: 'from-amber-500 to-amber-600',
    accentText: 'text-amber-700',
    accentBg: 'bg-amber-50',
    accentBorder: 'border-amber-200'
  },
  {
    value: 'projects', label: 'Projects',
    title: 'New project announcements',
    hint: 'PPAs signed, MW capacity added, or interconnection approvals.',
    icon: Zap,
    accent: 'from-blue-500 to-blue-600',
    accentText: 'text-blue-700',
    accentBg: 'bg-blue-50',
    accentBorder: 'border-blue-200'
  },
  {
    value: 'keywords', label: 'Custom',
    title: 'Free-form keywords',
    hint: 'Hand-curated query — use when none of the above signals fit.',
    icon: Tag,
    accent: 'from-gray-500 to-gray-600',
    accentText: 'text-gray-700',
    accentBg: 'bg-gray-50',
    accentBorder: 'border-gray-200'
  }
];

// ─── Tab 1: Find New Leads ──────────────────────────────────────────────────
function FindNewTab() {
  // Logged-in user — imports default to assigning the company to them so the
  // BDA doesn't have to pick their own name from a dropdown every time.
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    signal: 'hiring',
    role_titles: DEFAULT_SOLAR_ROLE_TITLES,
    location: 'United States',
    additional_keywords: '',
    enrich_with_apollo: true,
    exclude_known_companies: true,
    // Live web search is opt-in: it's slower (60-120s) but pulls fresh signals.
    // Default off → fast (~15-30s) knowledge-based discovery with inline ICP scoring.
    grounded: false
  });
  const [maxResults, setMaxResults] = useState(5);
  const [autoImport, setAutoImport] = useState(false);
  const [roundRobin, setRoundRobin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);
  const [bdas, setBdas] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  // Run history — every prior search run we ever did, with its candidates
  // loaded lazily on expand. Survives tab switches because the FindNewTab
  // stays mounted (see LeadGenAgent's display:none toggle).
  const [pastRuns, setPastRuns] = useState([]);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [candidatesByRun, setCandidatesByRun] = useState({}); // runId → candidates[]
  const [loadingCandsFor, setLoadingCandsFor] = useState({}); // runId → bool

  const refreshHistory = useCallback(async () => {
    try {
      const r = await leadGenAPI.listRuns(50);
      if (r?.success) {
        // Show only Gemini search runs in this tab. Enrich runs live in the Enrich tab.
        setPastRuns((r.data || []).filter(x => x.kind === 'search'));
      }
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    dashboardAPI.getBDEUsers?.().then(r => setBdas(r?.data || [])).catch(() => {});
    refreshHistory();
  }, [refreshHistory]);

  const runSearch = async () => {
    setLoading(true); setError(null); setCandidates([]); setRun(null);
    try {
      const out = await leadGenAPI.research({ filters, maxResults, autoImport, roundRobin });
      if (!out?.success) throw new Error(out?.error || 'Research failed');
      setRun({ run_id: out.run_id, summary: out.summary });
      setCandidates(out.candidates || []);
      refreshHistory();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const toggleRunExpansion = async (runId) => {
    if (expandedRunId === runId) { setExpandedRunId(null); return; }
    setExpandedRunId(runId);
    if (candidatesByRun[runId]) return; // cached
    setLoadingCandsFor(s => ({ ...s, [runId]: true }));
    try {
      const r = await leadGenAPI.candidatesForRun(runId);
      if (r?.success) setCandidatesByRun(s => ({ ...s, [runId]: r.data || [] }));
    } catch { /* leave cache empty */ }
    finally { setLoadingCandsFor(s => ({ ...s, [runId]: false })); }
  };

  // Import/dismiss from the history pane mutates the cached candidate list
  // so the row re-renders with the new status.
  const importFromHistory = async (runId, cand, bdeId = null) => {
    const assignToBdeId = bdeId ?? myBde?.id ?? null;
    setActionLoading(s => ({ ...s, [cand.id]: true }));
    try {
      const r = await leadGenAPI.importCandidate(cand.id, { assignToBdeId });
      if (!r?.success) throw new Error(r?.error || 'Import failed');
      setCandidatesByRun(s => ({
        ...s,
        [runId]: (s[runId] || []).map(c => c.id === cand.id ? { ...c, status: r.lead?.duplicate ? 'duplicate' : 'imported' } : c)
      }));
    } catch (e) { alert(`Import failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [cand.id]: false })); }
  };
  const dismissFromHistory = async (runId, cand) => {
    setActionLoading(s => ({ ...s, [cand.id]: true }));
    try {
      await leadGenAPI.dismissCandidate(cand.id);
      setCandidatesByRun(s => ({
        ...s,
        [runId]: (s[runId] || []).map(c => c.id === cand.id ? { ...c, status: 'dismissed' } : c)
      }));
    } catch (e) { alert(`Dismiss failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [cand.id]: false })); }
  };

  // Match the logged-in user to a bde_users row by name (same pattern the
  // lead modal uses). The matched id is the default assignee on imports.
  const myBde = useMemo(() => {
    if (!user?.name || !bdas.length) return null;
    const t = user.name.toLowerCase().trim();
    return bdas.find(b => (b.name || '').toLowerCase().trim() === t)
        || bdas.find(b => (b.name || '').toLowerCase().includes(t))
        || bdas.find(b => t.includes((b.name || '').toLowerCase()))
        || null;
  }, [user, bdas]);

  const importOne = async (cand, bdeId = null) => {
    // Default to the logged-in BDA when nothing is explicitly picked.
    const assignToBdeId = bdeId ?? myBde?.id ?? null;
    setActionLoading(s => ({ ...s, [cand.id]: true }));
    try {
      const r = await leadGenAPI.importCandidate(cand.id, { assignToBdeId });
      if (!r?.success) throw new Error(r?.error || 'Import failed');
      setCandidates(prev => prev.map(c => c.id === cand.id ? { ...c, status: r.lead?.duplicate ? 'duplicate' : 'imported' } : c));
    } catch (e) { alert(`Import failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [cand.id]: false })); }
  };

  const dismissOne = async (cand) => {
    setActionLoading(s => ({ ...s, [cand.id]: true }));
    try {
      await leadGenAPI.dismissCandidate(cand.id);
      setCandidates(prev => prev.map(c => c.id === cand.id ? { ...c, status: 'dismissed' } : c));
    } catch (e) { alert(`Dismiss failed: ${e.response?.data?.error || e.message}`); }
    finally { setActionLoading(s => ({ ...s, [cand.id]: false })); }
  };

  const activeSignal = SIGNAL_OPTIONS.find(s => s.value === filters.signal) || SIGNAL_OPTIONS[0];

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-base font-semibold text-gray-900">Find new solar leads</h4>
            <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
              Pick a <strong>discovery signal</strong> below. Gemini finds solar &amp; battery-storage <strong>asset operators</strong> and scores each against AMG's ICP — dropping manufacturers, installers, recruiters &amp; non-operators. Apollo enriches the survivors. Companies already in your pipeline are auto-excluded. Fast by default; tick <em>Live web search</em> for fresher (but slower) signals.
            </p>
          </div>
        </div>

        {/* Signal picker — card grid driving which prompt strategy Gemini runs.
            Visually grouped by colour so the BDA can scan ICP signals at a glance. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
          {SIGNAL_OPTIONS.map(s => {
            const Icon = s.icon;
            const isActive = filters.signal === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setFilters(f => ({ ...f, signal: s.value }))}
                className={`relative text-left p-3 rounded-lg border-2 transition-all ${
                  isActive
                    ? `${s.accentBorder} ${s.accentBg} ring-1 ring-offset-0 shadow-sm`
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
                title={s.hint}
              >
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br ${s.accent} text-white shadow-sm mb-2`}>
                  <Icon size={16} />
                </div>
                <div className={`text-sm font-semibold ${isActive ? s.accentText : 'text-gray-900'}`}>{s.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 leading-snug line-clamp-2">{s.title}</div>
                {isActive && (
                  <div className="absolute top-2 right-2">
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${s.accent}`} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className={`mb-4 -mt-2 px-3 py-2 rounded-lg ${activeSignal.accentBg} border ${activeSignal.accentBorder}`}>
          <p className={`text-[12px] ${activeSignal.accentText} font-medium`}>
            <strong>{activeSignal.title}:</strong> <span className="font-normal opacity-90">{activeSignal.hint}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Role titles only matter for the hiring signal. */}
          {filters.signal === 'hiring' && (
            <ChipInput
              label="Target role titles (AMG ICP defaults)"
              value={filters.role_titles}
              onChange={(v) => setFilters(f => ({ ...f, role_titles: v }))}
              placeholder="Solar ROCC Analyst, Control room operator, …"
              hint="Comma-separated. Edit to broaden or narrow the hiring signals."
            />
          )}
          <div>
            <label className={LABEL_CLS}>Location (country / region / state)</label>
            <input type="text" value={filters.location} onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))} placeholder="United States, India, Texas" className={INPUT_CLS} />
          </div>
          <div className={filters.signal === 'hiring' ? 'md:col-span-2' : ''}>
            <label className={LABEL_CLS}>
              {filters.signal === 'keywords' ? 'Keywords (required for this mode)' : 'Additional keywords (optional)'}
            </label>
            <input
              type="text"
              value={filters.additional_keywords}
              onChange={(e) => setFilters(f => ({ ...f, additional_keywords: e.target.value }))}
              placeholder={
                filters.signal === 'ma'       ? 'optional: e.g. "utility-scale acquisitions"' :
                filters.signal === 'funding'  ? 'optional: e.g. "Series B", "project finance"' :
                filters.signal === 'projects' ? 'optional: e.g. "200MW", "PPA signed"' :
                filters.signal === 'keywords' ? 'BESS operators, virtual power plants, …' :
                                                'utility-scale, BESS, IPP'
              }
              className={INPUT_CLS}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input
            type="checkbox"
            checked={filters.exclude_known_companies}
            onChange={(e) => setFilters(f => ({ ...f, exclude_known_companies: e.target.checked }))}
            className="rounded"
          />
          Exclude companies already in our pipeline (recommended)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className={LABEL_CLS}>Companies per run</label>
            <select value={maxResults} onChange={(e) => setMaxResults(parseInt(e.target.value, 10))} className={INPUT_CLS}>
              <option value={3}>3</option>
              <option value={5}>5 (default)</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </div>
          <label className="flex items-center gap-2 mt-5 text-sm">
            <input type="checkbox" checked={filters.enrich_with_apollo} onChange={(e) => setFilters(f => ({ ...f, enrich_with_apollo: e.target.checked }))} className="rounded" />
            Enrich with Apollo (free tier, ~1 credit per company)
          </label>
          <label className="flex items-center gap-2 mt-5 text-sm">
            <input type="checkbox" checked={autoImport} onChange={(e) => setAutoImport(e.target.checked)} className="rounded" />
            Auto-import (skip review)
          </label>
          {autoImport && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={roundRobin} onChange={(e) => setRoundRobin(e.target.checked)} className="rounded" />
              Round-robin to active BDAs
            </label>
          )}
          <label className="flex items-center gap-2 mt-1 text-sm md:col-span-3" title="Off = fast (~15-30s), uses the model's knowledge of real operators. On = pulls live web evidence (job posts, news) but takes 1-2 minutes.">
            <input type="checkbox" checked={filters.grounded} onChange={(e) => setFilters(f => ({ ...f, grounded: e.target.checked }))} className="rounded" />
            <span>Live web search <span className="text-gray-400">— fresher signals, but slower (1–2 min). Off by default for speed.</span></span>
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={runSearch} disabled={loading} className={BTN_PRIMARY}>
            {loading ? (filters.grounded ? 'Searching the live web…' : 'Finding ICP-matched companies…') : 'Find leads'}
          </button>
          <span className="text-[11px] text-gray-500">
            {filters.grounded
              ? 'Live web search — this can take 1–2 minutes.'
              : 'Fast mode (~15–30s) — Gemini matches real solar/BESS operators to the ICP. Tick “Live web search” for fresher signals.'}
          </span>
        </div>
        {error && (
          <div className="mt-3 border border-red-200 bg-red-50 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>
        )}
      </div>

      {run && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-800">Latest run · {candidates.length} compan{candidates.length === 1 ? 'y' : 'ies'}</h4>
            <span className="text-xs text-gray-500">Run {run.run_id.slice(0, 8)}… · {run.summary?.auto_imported || 0} auto-imported</span>
          </div>
          {candidates.length === 0 && <p className="text-sm text-gray-500">No matches. Loosen the filters and try again.</p>}
          <CandidateList
            candidates={candidates}
            bdas={bdas}
            myBde={myBde}
            actionLoading={actionLoading}
            onImport={(c, bdeId) => importOne(c, bdeId)}
            onDismiss={(c) => dismissOne(c)}
          />
        </div>
      )}

      <PreviousSearchesPane
        runs={pastRuns}
        expandedRunId={expandedRunId}
        onToggle={toggleRunExpansion}
        candidatesByRun={candidatesByRun}
        loadingCandsFor={loadingCandsFor}
        bdas={bdas}
        myBde={myBde}
        actionLoading={actionLoading}
        onImport={(runId, cand, bdeId) => importFromHistory(runId, cand, bdeId)}
        onDismiss={(runId, cand) => dismissFromHistory(runId, cand)}
      />
    </div>
  );
}

// Pretty colour-from-name generator for the company avatar.
// Same string → same colour so the same company always looks consistent across views.
function avatarColour(name) {
  const palette = [
    'from-emerald-500 to-emerald-600',
    'from-blue-500 to-blue-600',
    'from-amber-500 to-amber-600',
    'from-purple-500 to-purple-600',
    'from-rose-500 to-rose-600',
    'from-cyan-500 to-cyan-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600'
  ];
  let h = 0;
  for (const c of (name || '')) h = ((h << 5) - h + c.charCodeAt(0)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}
function avatarInitials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// ─── Reusable: single candidate card with Import / Dismiss controls ─────────
function CandidateList({ candidates, bdas, myBde, actionLoading, onImport, onDismiss }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {candidates.map(c => {
        const org = c.payload?.org || {};
        const person = c.payload?.person || {};
        const raw = org.raw || {};
        const statusStyle =
          c.status === 'imported'  ? { ring: 'ring-emerald-300', accent: 'bg-emerald-100 text-emerald-700', label: 'Imported' } :
          c.status === 'duplicate' ? { ring: 'ring-amber-300',   accent: 'bg-amber-100 text-amber-700',     label: 'Duplicate' } :
          c.status === 'dismissed' ? { ring: 'ring-gray-300',    accent: 'bg-gray-100 text-gray-500',        label: 'Dismissed' } :
                                     { ring: 'ring-gray-200',    accent: 'bg-blue-50 text-blue-700',         label: 'New' };
        const disabled = c.status !== 'pending' || actionLoading[c.id];
        const location = [org.city, org.state, org.country].filter(Boolean).join(', ');
        // ICP fit metadata from the Gemini verification pass.
        const fitScore = (raw.fit_score ?? null);
        const fitStyle = fitScore == null ? null
          : fitScore >= 80 ? 'bg-emerald-100 text-emerald-700'
          : fitScore >= 60 ? 'bg-lime-100 text-lime-700'
          : fitScore >= 40 ? 'bg-amber-100 text-amber-700'
          : 'bg-red-100 text-red-700';
        const assetLabel = raw.asset_class && raw.asset_class !== 'unknown' ? raw.asset_class : null;
        return (
          <div
            key={c.id}
            className={`relative bg-white border border-gray-200 rounded-xl p-4 shadow-sm ring-1 ${statusStyle.ring} hover:shadow-md transition-shadow ${c.status !== 'pending' ? 'opacity-90' : ''}`}
          >
            <div className="flex items-start gap-3">
              {/* Company avatar — initials over a gradient, deterministic by name */}
              <div className={`flex-shrink-0 w-11 h-11 rounded-lg bg-gradient-to-br ${avatarColour(org.company_name)} flex items-center justify-center text-white font-semibold text-sm shadow-sm`}>
                {avatarInitials(org.company_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h5 className="font-semibold text-gray-900 truncate">{org.company_name || '—'}</h5>
                    {location && (
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin size={11} /> {location}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {fitScore != null && (
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${fitStyle}`}
                        title={`ICP fit score${raw.fit_verdict ? ` — ${raw.fit_verdict}` : ''}`}
                      >
                        {fitScore}% fit
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${statusStyle.accent}`}>
                      {statusStyle.label}
                    </span>
                  </div>
                </div>

                {/* Meta chips row */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-600">
                  {org.company_url && (
                    <a href={org.company_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                      <ExternalLink size={10} /> Website
                    </a>
                  )}
                  {org.linkedin_url && (
                    <a href={org.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                      <ExternalLink size={10} /> LinkedIn
                    </a>
                  )}
                  {org.employee_count ? (
                    <span className="inline-flex items-center gap-1"><Users size={10} /> {org.employee_count}</span>
                  ) : null}
                  {org.founded_year ? <span>· est. {org.founded_year}</span> : null}
                  {org.core_business && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                      <Building2 size={10} /> {org.core_business}
                    </span>
                  )}
                  {assetLabel && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 uppercase font-semibold tracking-wide">
                      {assetLabel}
                    </span>
                  )}
                  {raw.company_type && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                      {raw.company_type}
                    </span>
                  )}
                  {raw.portfolio_scale && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                      <Zap size={10} /> {raw.portfolio_scale}
                    </span>
                  )}
                </div>

                {/* Signal evidence */}
                {raw.role_title && (
                  <div className="mt-2 px-2.5 py-1.5 rounded-md bg-emerald-50 border border-emerald-100">
                    <div className="text-[11px] font-medium text-emerald-800 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1">
                        <Briefcase size={11} /> Hiring: {raw.role_title}
                      </span>
                      {raw.job_url && (
                        <a href={raw.job_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-0.5">
                          view <ExternalLink size={9} />
                        </a>
                      )}
                    </div>
                    {raw.evidence && <div className="text-[10.5px] text-emerald-700/80 italic mt-0.5">{raw.evidence}</div>}
                  </div>
                )}

                {/* ICP fit verdict from the verification pass */}
                {(raw.fit_reason || (raw.fit_signals && raw.fit_signals.length > 0)) && (
                  <div className="mt-2 px-2.5 py-1.5 rounded-md bg-indigo-50/70 border border-indigo-100">
                    {raw.fit_reason && (
                      <div className="text-[10.5px] text-indigo-800">
                        <span className="font-semibold">Why it fits:</span> {raw.fit_reason}
                      </div>
                    )}
                    {raw.fit_signals && raw.fit_signals.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {raw.fit_signals.slice(0, 6).map((s, i) => (
                          <span key={i} className="text-[9.5px] px-1.5 py-0.5 rounded-full bg-white text-indigo-600 border border-indigo-200">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* POC (LinkedIn job poster, when available) */}
                {person?.poc_name && (
                  <div className="mt-2 text-[11px] text-gray-700">
                    <span className="font-medium text-gray-900">POC:</span> {person.poc_name}
                    {person.designation && <span className="text-gray-500"> — {person.designation}</span>}
                    {person.poc_linkedin_url && (
                      <a href={person.poc_linkedin_url} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">
                        LinkedIn
                      </a>
                    )}
                  </div>
                )}

                {/* Actions row — primary CTA imports to the logged-in BDA;
                    secondary dropdown lets you reassign to a teammate instead. */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => onImport(c, myBde?.id ?? null)}
                    disabled={disabled}
                    className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed"
                    title={myBde ? `Import as a new Pre-Sales lead assigned to ${myBde.name}` : 'Import as a new Pre-Sales lead'}
                  >
                    + Import {myBde ? `to ${myBde.name}` : 'as new lead'}
                  </button>
                  <select
                    defaultValue=""
                    disabled={disabled}
                    onChange={(e) => onImport(c, e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
                    title="Assign to a different BDA"
                  >
                    <option value="">Assign to…</option>
                    {bdas.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  <button
                    onClick={() => onDismiss(c)}
                    disabled={disabled}
                    className="text-xs text-gray-500 hover:text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    title="Dismiss this candidate"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Previous searches: collapsible history of every Gemini run ─────────────
function PreviousSearchesPane({ runs, expandedRunId, onToggle, candidatesByRun, loadingCandsFor, bdas, myBde, actionLoading, onImport, onDismiss }) {
  if (!runs?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-gray-800 mb-1">Previous searches ({runs.length})</h4>
      <p className="text-xs text-gray-500 mb-3">Every search you've ever run, newest first. Click a row to see the companies that came back; import or dismiss them inline.</p>
      <div className="space-y-1.5">
        {runs.map(r => {
          const isOpen = expandedRunId === r.id;
          const isLoading = loadingCandsFor[r.id];
          const cands = candidatesByRun[r.id] || [];
          const statusIcon = r.status === 'success' ? <CheckCircle2 size={14} className="text-green-600" />
                            : r.status === 'failed' ? <AlertCircle size={14} className="text-red-600" />
                            : <Clock size={14} className="text-amber-600" />;
          return (
            <div key={r.id} className="border border-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => onToggle(r.id)}
                className="w-full text-left px-3 py-2 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 text-sm">
                  {statusIcon}
                  <span className="text-xs text-gray-500">{new Date(r.started_at).toLocaleString()}</span>
                  <span className="font-medium">{r.results_count} compan{r.results_count === 1 ? 'y' : 'ies'}</span>
                  <span className="text-xs text-gray-500">· {r.trigger}</span>
                  {r.credits_used > 0 && <span className="text-xs text-gray-500">· {r.credits_used} credits</span>}
                  {r.error_message && <span className="text-xs text-red-600 truncate max-w-xs" title={r.error_message}>· {r.error_message}</span>}
                </div>
                <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                  {isOpen ? 'Hide' : 'Show'} companies
                  <ChevronRight size={12} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 border-t border-gray-100">
                  {isLoading && <p className="text-sm text-gray-500 mt-2">Loading companies…</p>}
                  {!isLoading && cands.length === 0 && <p className="text-sm text-gray-500 mt-2">No companies recorded for this run.</p>}
                  {!isLoading && cands.length > 0 && (
                    <div className="mt-2">
                      <CandidateList
                        candidates={cands}
                        bdas={bdas}
                        myBde={myBde}
                        actionLoading={actionLoading}
                        onImport={(c, bdeId) => onImport(r.id, c, bdeId)}
                        onDismiss={(c) => onDismiss(r.id, c)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tab 2: Enrich Existing ─────────────────────────────────────────────────
function EnrichTab() {
  const [filters, setFilters] = useState({ date_from: '', date_to: '', year: '', stage: '', bde_name: '' });
  const [maxResults, setMaxResults] = useState(10);
  const [missingOnly, setMissingOnly] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [bdas, setBdas] = useState([]);
  // Previous enrich runs — fetched on mount + refreshed after each new run.
  const [pastEnrichRuns, setPastEnrichRuns] = useState([]);
  const [expandedRunId, setExpandedRunId] = useState(null);

  const refreshHistory = useCallback(async () => {
    try {
      const r = await leadGenAPI.listRuns(50);
      if (r?.success) setPastEnrichRuns((r.data || []).filter(x => x.kind === 'enrich'));
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    dashboardAPI.getBDEUsers?.().then(r => setBdas(r?.data || [])).catch(() => {});
    refreshHistory();
  }, [refreshHistory]);

  const run = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '' && v != null));
      const out = await leadGenAPI.enrichBulk({ filters: cleanFilters, dryRun, missingOnly, maxResults });
      if (!out?.success) throw new Error(out?.error || 'Enrich failed');
      setResult(out);
      refreshHistory();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Pick which leads to enrich</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={LABEL_CLS}>Lead date from</label>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Lead date to</label>
            <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Or full year</label>
            <input type="number" placeholder="2025" value={filters.year} onChange={(e) => setFilters(f => ({ ...f, year: e.target.value }))} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Stage</label>
            <select value={filters.stage} onChange={(e) => setFilters(f => ({ ...f, stage: e.target.value }))} className={INPUT_CLS}>
              <option value="">Any stage</option>
              {['New', 'Contacted', 'Initial call', 'Demo call', 'Follow-up 1', 'Follow-up 2', 'Proposal', 'Negotiation', 'Onboarded'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>BDA</label>
            <select value={filters.bde_name} onChange={(e) => setFilters(f => ({ ...f, bde_name: e.target.value }))} className={INPUT_CLS}>
              <option value="">Any BDA</option>
              {bdas.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Max leads per run</label>
            <select value={maxResults} onChange={(e) => setMaxResults(parseInt(e.target.value, 10))} className={INPUT_CLS}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} className="rounded" />
            Only fill <em>missing</em> fields (recommended)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
            Dry-run preview (no writes)
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button onClick={run} disabled={loading} className={BTN_PRIMARY}>
            {loading ? 'Calling Apollo…' : (dryRun ? 'Preview changes' : 'Run + apply')}
          </button>
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </div>

      {result && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Latest run · {result.run_id?.slice(0, 8)}…</h4>
          <p className="text-xs text-gray-500 mb-3">
            Scanned {result.summary?.candidates_scanned || 0} leads · {result.summary?.dry_run ? 'previewed' : 'updated'} {result.summary?.updated_or_previewed || 0}.
          </p>
          <EnrichResultRows results={result.results || []} />
        </div>
      )}

      {pastEnrichRuns.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 mb-1">Previous enrich runs ({pastEnrichRuns.length})</h4>
          <p className="text-xs text-gray-500 mb-3">Every enrich pass you've run before, oldest at the bottom. Expand to see which leads were touched.</p>
          <div className="space-y-1.5">
            {pastEnrichRuns.map(r => {
              const isOpen = expandedRunId === r.id;
              const details = r.summary?.details || [];
              const statusIcon = r.status === 'success' ? <CheckCircle2 size={14} className="text-green-600" />
                                : r.status === 'failed' ? <AlertCircle size={14} className="text-red-600" />
                                : <Clock size={14} className="text-amber-600" />;
              return (
                <div key={r.id} className="border border-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setExpandedRunId(isOpen ? null : r.id)}
                    className="w-full text-left px-3 py-2 flex flex-wrap items-center justify-between gap-2 hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      {statusIcon}
                      <span className="text-xs text-gray-500">{new Date(r.started_at).toLocaleString()}</span>
                      <span className="font-medium">{r.results_count} updated</span>
                      <span className="text-xs text-gray-500">· {r.trigger}</span>
                      {r.credits_used > 0 && <span className="text-xs text-gray-500">· {r.credits_used} credits</span>}
                      {r.summary?.dry_run && <span className="text-xs text-blue-600">· dry-run</span>}
                      {r.error_message && <span className="text-xs text-red-600 truncate max-w-xs" title={r.error_message}>· {r.error_message}</span>}
                    </div>
                    <span className="text-xs text-gray-500">{isOpen ? 'Hide' : 'Show'} details</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      {details.length === 0 && <p className="text-sm text-gray-500 mt-2">No detail rows recorded.</p>}
                      {details.length > 0 && <div className="mt-2"><EnrichResultRows results={details} /></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable enrich-result rows — used both for the latest run and history.
function EnrichResultRows({ results }) {
  return (
    <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
      {results.map((r, i) => (
        <div key={i} className="border border-gray-100 rounded-lg px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-medium text-gray-900">{r.company_name || r.lead_id}</div>
            <div className="text-xs">
              {r.error && <span className="text-red-600">{r.error}</span>}
              {r.skipped && <span className="text-gray-500">{r.reason}</span>}
              {r.dry_run && <span className="text-blue-700">would fill: {Object.keys(r.would_update || {}).join(', ') || 'nothing'}</span>}
              {r.updated && <span className="text-green-700">filled: {Object.keys(r.applied || {}).filter(k => k !== 'updated_at').join(', ')}</span>}
              {r.updated === false && !r.error && <span className="text-gray-500">no change</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tab 3: Schedules & Runs ────────────────────────────────────────────────
function SchedulesTab() {
  const [schedules, setSchedules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([leadGenAPI.listSchedules(), leadGenAPI.listRuns(15)]);
      if (s?.success) setSchedules(s.data || []);
      if (r?.success) setRuns(r.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleActive = async (s) => {
    await leadGenAPI.updateSchedule(s.id, { active: !s.active });
    refresh();
  };
  const runNow = async (s) => {
    if (!window.confirm(`Run "${s.name}" now? This will consume Apollo credits.`)) return;
    try {
      const out = await leadGenAPI.runScheduleNow(s.id);
      alert(out?.success ? `Run finished. ${out.summary?.candidates_found || out.summary?.updated_or_previewed || 0} results.` : `Failed: ${out?.error}`);
    } catch (e) { alert(`Failed: ${e.response?.data?.error || e.message}`); }
    refresh();
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    await leadGenAPI.deleteSchedule(s.id);
    refresh();
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-800">Schedules ({schedules.length})</h4>
          <button onClick={() => setShowCreate(true)} className={BTN_PRIMARY}>+ New schedule</button>
        </div>
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && schedules.length === 0 && (
          <p className="text-sm text-gray-500">No schedules yet. Create one to have the agent run on cron — e.g. "Daily 9am: search for US solar installers" or "Weekly Mondays: enrich leads from last month."</p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {schedules.map(s => {
            const kindMeta = s.kind === 'search'
              ? { label: 'Research', icon: Search, accent: 'bg-emerald-100 text-emerald-700' }
              : { label: 'Enrich',   icon: RefreshCw, accent: 'bg-blue-100 text-blue-700' };
            const KIcon = kindMeta.icon;
            return (
              <div key={s.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className={`w-9 h-9 rounded-lg ${kindMeta.accent} flex items-center justify-center flex-shrink-0`}>
                      <KIcon size={16} />
                    </div>
                    <div className="min-w-0">
                      <h5 className="font-semibold text-gray-900 truncate">{s.name}</h5>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                          {s.active ? '● active' : '○ paused'}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">{kindMeta.label}</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => remove(s)} className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-red-50 flex-shrink-0" title="Delete schedule">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="text-xs text-gray-600 space-y-0.5 mb-3">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={11} className="text-gray-400" />
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">{s.cron_expr}</code>
                    <span className="text-gray-400">{s.timezone}</span>
                  </div>
                  <div className="text-gray-500">
                    Max {s.max_results} per run
                    {s.kind === 'search' && s.auto_import && <span className="ml-1">· auto-import{s.round_robin ? ' (round-robin)' : ''}</span>}
                  </div>
                  {s.last_run_at && (
                    <div className="text-gray-400 text-[11px]">Last run {new Date(s.last_run_at).toLocaleString()}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <button onClick={() => runNow(s)} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] text-xs font-medium">
                    <Play size={12} /> Run now
                  </button>
                  <button onClick={() => toggleActive(s)} className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium">
                    {s.active ? 'Pause' : 'Activate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Recent runs ({runs.length})</h4>
        {runs.length === 0 && <p className="text-sm text-gray-500">No runs yet.</p>}
        <div className="space-y-1.5">
          {runs.map(r => {
            const icon = r.status === 'success' ? <CheckCircle2 size={14} className="text-green-600" /> :
                         r.status === 'failed' ? <AlertCircle size={14} className="text-red-600" /> :
                         <Clock size={14} className="text-amber-600" />;
            return (
              <div key={r.id} className="border border-gray-100 rounded-lg px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {icon}
                  <span className="font-medium">{r.kind}</span>
                  <span className="text-xs text-gray-500">· {r.trigger}</span>
                  <span className="text-xs text-gray-500">· {new Date(r.started_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-gray-600">
                  {r.results_count} results · {r.credits_used} credits
                  {r.error_message && <span className="ml-2 text-red-600 truncate max-w-xs inline-block align-bottom" title={r.error_message}>· {r.error_message}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreate && <CreateScheduleModal onClose={() => setShowCreate(false)} onCreated={refresh} />}
    </div>
  );
}

// ─── Create-schedule modal ──────────────────────────────────────────────────
function CreateScheduleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    kind: 'search',
    cron_expr: '0 9 * * *',
    timezone: 'America/Chicago',
    active: true,
    auto_import: false,
    round_robin: true,
    max_results: 5,
    // Gemini research filters (kind=search; backed by Gemini via _source flag)
    role_titles: DEFAULT_SOLAR_ROLE_TITLES.join(', '),
    location: 'United States',
    additional_keywords: '',
    enrich_with_apollo: true,
    // enrich filters
    date_from: '',
    date_to: '',
    year: '',
    stage: '',
    bde_name: '',
    missing_only: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!form.name) return setError('Name is required');
    setSaving(true); setError(null);
    try {
      const filters = form.kind === 'search'
        ? {
            // _source: 'gemini' tells runSchedule to use runResearch (Gemini + Google Search)
            // rather than runSearch (Apollo people-search, paywalled on free tier).
            _source: 'gemini',
            role_titles: form.role_titles.split(',').map(s => s.trim()).filter(Boolean),
            location: form.location || undefined,
            additional_keywords: form.additional_keywords || undefined,
            enrich_with_apollo: form.enrich_with_apollo
          }
        : {
            date_from: form.date_from || undefined,
            date_to: form.date_to || undefined,
            year: form.year || undefined,
            stage: form.stage || undefined,
            bde_name: form.bde_name || undefined,
            missing_only: form.missing_only
          };
      const out = await leadGenAPI.createSchedule({
        name: form.name,
        kind: form.kind,
        filters,
        cron_expr: form.cron_expr,
        timezone: form.timezone,
        active: form.active,
        auto_import: form.auto_import,
        round_robin: form.round_robin,
        max_results: form.max_results
      });
      if (!out?.success) throw new Error(out?.error || 'Failed');
      onCreated();
      onClose();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900">Create schedule</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLS}>Name</label>
              <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Daily: US solar installers" className={INPUT_CLS} />
            </div>
            <div>
              <label className={LABEL_CLS}>Kind</label>
              <select value={form.kind} onChange={(e) => setForm(f => ({ ...f, kind: e.target.value }))} className={INPUT_CLS}>
                <option value="search">Research (find new solar leads via Gemini)</option>
                <option value="enrich">Enrich (fill missing fields on existing leads via Apollo)</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLS}>Cron expression</label>
              <input value={form.cron_expr} onChange={(e) => setForm(f => ({ ...f, cron_expr: e.target.value }))} className={INPUT_CLS} placeholder="0 9 * * *" />
              <p className="text-[11px] text-gray-500 mt-1">Examples: <code>0 9 * * *</code> daily 9am · <code>0 9 * * 1-5</code> weekdays 9am · <code>0 */6 * * *</code> every 6h.</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Max results per run</label>
              <select value={form.max_results} onChange={(e) => setForm(f => ({ ...f, max_results: parseInt(e.target.value, 10) }))} className={INPUT_CLS}>
                {[3, 5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {form.kind === 'search' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <div className="md:col-span-2">
                <label className={LABEL_CLS}>Target role titles (comma-separated)</label>
                <input value={form.role_titles} onChange={(e) => setForm(f => ({ ...f, role_titles: e.target.value }))} placeholder="Solar ROCC Analyst, Control room operator…" className={INPUT_CLS} />
                <p className="text-[11px] text-gray-500 mt-1">Companies hiring for these roles are AMG's ICP.</p>
              </div>
              <div><label className={LABEL_CLS}>Location</label><input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} placeholder="United States, India, Texas" className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>Additional keywords</label><input value={form.additional_keywords} onChange={(e) => setForm(f => ({ ...f, additional_keywords: e.target.value }))} placeholder="utility-scale, IPP, BESS" className={INPUT_CLS} /></div>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={form.enrich_with_apollo} onChange={(e) => setForm(f => ({ ...f, enrich_with_apollo: e.target.checked }))} className="rounded" />
                Enrich each result via Apollo (free tier; ~1 credit per company)
              </label>
              <label className="flex items-center gap-2 text-sm md:col-span-2 mt-2">
                <input type="checkbox" checked={form.auto_import} onChange={(e) => setForm(f => ({ ...f, auto_import: e.target.checked }))} className="rounded" />
                Auto-import results as leads
              </label>
              {form.auto_import && (
                <label className="flex items-center gap-2 text-sm md:col-span-2">
                  <input type="checkbox" checked={form.round_robin} onChange={(e) => setForm(f => ({ ...f, round_robin: e.target.checked }))} className="rounded" />
                  Round-robin across active BDAs
                </label>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <div><label className={LABEL_CLS}>Date from</label><input type="date" value={form.date_from} onChange={(e) => setForm(f => ({ ...f, date_from: e.target.value }))} className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>Date to</label><input type="date" value={form.date_to} onChange={(e) => setForm(f => ({ ...f, date_to: e.target.value }))} className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>Or full year</label><input type="number" placeholder="2025" value={form.year} onChange={(e) => setForm(f => ({ ...f, year: e.target.value }))} className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>Stage</label><input value={form.stage} onChange={(e) => setForm(f => ({ ...f, stage: e.target.value }))} placeholder="New" className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>BDA</label><input value={form.bde_name} onChange={(e) => setForm(f => ({ ...f, bde_name: e.target.value }))} placeholder="Hima Sree" className={INPUT_CLS} /></div>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={form.missing_only} onChange={(e) => setForm(f => ({ ...f, missing_only: e.target.checked }))} className="rounded" />
                Only fill missing fields
              </label>
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
          <button onClick={onClose} className={BTN_SECONDARY}>Cancel</button>
          <button onClick={submit} disabled={saving} className={BTN_PRIMARY}>{saving ? 'Saving…' : 'Save schedule'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Default export ─────────────────────────────────────────────────────────
export default function LeadGenAgent() {
  const [tab, setTab] = useState('find');
  // Once a sub-tab is visited we keep it mounted with display:none so its
  // state (current candidates, dry-run preview, schedule list) survives
  // switching away and coming back. Otherwise React unmounts and we lose
  // whatever the user just fetched.
  const [visited, setVisited] = useState(new Set(['find']));
  useEffect(() => { setVisited(s => s.has(tab) ? s : new Set([...s, tab])); }, [tab]);
  const activeMeta = useMemo(() => TABS.find(t => t.key === tab), [tab]);

  return (
    <div className="space-y-5">
      {/* Hero header — green gradient banner that matches AMG's brand colour
          and grounds the page. Apollo health pill stays top-right. */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#679632] via-[#587f2b] to-[#3f5d1e] text-white shadow-sm">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 60%, white 1px, transparent 1px)', backgroundSize: '40px 40px, 60px 60px' }} />
        <div className="relative px-6 py-5 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="bg-white/15 backdrop-blur rounded-lg p-2.5 flex items-center justify-center">
              <Sparkles size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Lead Gen Agent</h2>
              <p className="text-sm text-white/85 mt-0.5 max-w-2xl">
                Discover net-new solar leads with Gemini + Google Search · enrich existing ones via Apollo · schedule it all on cron.
              </p>
            </div>
          </div>
          <ApolloHealthPill />
        </div>
      </div>

      {/* Tab pill bar — segmented look with the active tab on a green pill.
          Reads as one cohesive control instead of three loose buttons. */}
      <div className="inline-flex bg-gray-100 rounded-xl p-1 shadow-inner">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                isActive
                  ? 'bg-white text-[#679632] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {activeMeta?.blurb && (
        <p className="text-xs text-gray-500 -mt-2 ml-1">{activeMeta.blurb}</p>
      )}

      {/* All visited tabs stay mounted (display toggled) so state persists across switches. */}
      {visited.has('find')      && <div style={{ display: tab === 'find'      ? 'block' : 'none' }}><FindNewTab /></div>}
      {visited.has('enrich')    && <div style={{ display: tab === 'enrich'    ? 'block' : 'none' }}><EnrichTab /></div>}
      {visited.has('schedules') && <div style={{ display: tab === 'schedules' ? 'block' : 'none' }}><SchedulesTab /></div>}
    </div>
  );
}
